import React from 'react';
import { Typography, Grid, IconButton, InputAdornment, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'next-i18next';
import {
  FilterAccordion,
  FilterAccordionSummary,
  FilterAccordionDetails,
  PriceInput,
  PriceInputContainer,
  PriceButton,
  Divider
} from '../styledComponents';
import { ExpandMoreIcon } from './ExpandMoreIcon';

interface AreaSectionProps {
  isExpanded: boolean;
  isVisible: boolean;
  onToggle: (event: React.MouseEvent) => void;
  areaMin: number;
  areaMax: number;
  areaMinInput: string;
  areaMaxInput: string;
  areaRangesMin: number[];
  areaRangesMax: number[];
  onAreaMinInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAreaMaxInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAreaMinSelect: (area: number) => void;
  onAreaMaxSelect: (area: number) => void;
  onAreaMinInputClear?: () => void; // Add clear handler for min input
  onAreaMaxInputClear?: () => void; // Add clear handler for max input
  onCollapseAfterSelect?: () => void; // เพิ่ม callback สำหรับปิด accordion หลังเลือก
}

export const AreaSection: React.FC<AreaSectionProps> = ({
  isExpanded,
  isVisible,
  onToggle,
  areaMin,
  areaMax,
  areaMinInput,
  areaMaxInput,
  areaRangesMin,
  areaRangesMax,
  onAreaMinInputChange,
  onAreaMaxInputChange,
  onAreaMinSelect,
  onAreaMaxSelect,
  onAreaMinInputClear,
  onAreaMaxInputClear,
  onCollapseAfterSelect,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle area min select with auto-close
  const handleAreaMinSelect = (area: number) => {
    onAreaMinSelect(area);
    
    // Auto-close accordion หลังเลือก preset area
    if (onCollapseAfterSelect) {
      setTimeout(() => {
        onCollapseAfterSelect();
      }, 150);
    }
  };

  // Handle area max select with auto-close
  const handleAreaMaxSelect = (area: number) => {
    onAreaMaxSelect(area);
    
    // Auto-close accordion หลังเลือก preset area
    if (onCollapseAfterSelect) {
      setTimeout(() => {
        onCollapseAfterSelect();
      }, 150);
    }
  };

  // สร้าง label สำหรับแสดงค่าพื้นที่ที่เลือก
  const getAreaLabel = () => {
    if (areaMin && areaMax && areaMin !== 0 && areaMax !== 1000) {
      return `${areaMin} - ${areaMax} ตร.ม.`;
    } else if (areaMin && areaMin !== 0) {
      return `${t('from')} ${areaMin} ตร.ม.`;
    } else if (areaMax && areaMax !== 1000) {
      return `${t('up_to')} ${areaMax} ตร.ม.`;
    }
    return null;
  };

  const areaLabel = getAreaLabel();

  if (!isVisible) return null;

  return (
    <>
      <FilterAccordion className={isExpanded ? 'expanded' : ''}>
        <FilterAccordionSummary 
          className={isExpanded ? 'expanded' : ''}
          onClick={onToggle}
        >
          <Typography className="section-title" variant="subtitle1" fontWeight="medium">
            {isMobile && areaLabel ? (
              <>
                <span style={{ color: '#919192' }}>{t('useable_area')}</span>
                <span style={{ color: '#000000', fontWeight: 400 }}> : {areaLabel}</span>
              </>
            ) : (
              t('useable_area')
            )}
          </Typography>
          <ExpandMoreIcon className="expand-icon" />
        </FilterAccordionSummary>
        {isExpanded && (
          <FilterAccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <PriceInputContainer>
                  <PriceInput
                    placeholder="พื้นที่ขั้นต่ำ"
                    value={areaMinInput}
                    onChange={onAreaMinInputChange}
                    endAdornment={areaMinInput && onAreaMinInputClear && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={onAreaMinInputClear}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  />
                </PriceInputContainer>
                <div style={{ marginTop: '8px' }}>
                  {areaRangesMin.map((area) => (
                    <PriceButton 
                      key={`area-min-${area}`}
                      onClick={() => handleAreaMinSelect(area)}
                    >
                      {area === 0 ? 'ไม่ระบุ' : area + ' ตร.ม.'}
                    </PriceButton>
                  ))}
                </div>
              </Grid>
              <Grid item xs={6}>
                <PriceInputContainer>
                  <PriceInput
                    placeholder="พื้นที่สูงสุด"
                    value={areaMaxInput}
                    onChange={onAreaMaxInputChange}
                    endAdornment={areaMaxInput && onAreaMaxInputClear && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={onAreaMaxInputClear}
                          edge="end"
                          size="small"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )}
                  />
                </PriceInputContainer>
                <div style={{ marginTop: '8px' }}>
                  {areaRangesMax.map((area) => (
                    <PriceButton 
                      key={`area-max-${area}`}
                      onClick={() => handleAreaMaxSelect(area)}
                    >
                      {area + ' ตร.ม.'}
                    </PriceButton>
                  ))}
                </div>
              </Grid>
            </Grid>
          </FilterAccordionDetails>
        )}
      </FilterAccordion>
      <Divider />
    </>
  );
}; 